import { useState, useEffect, useRef } from 'react';
import { useHydrated } from '../hooks/useHydrated';
import { resolvedComponents } from '../hydrationPreload';
import { Search, X, Share2 } from 'lucide-react';
import SearchResults from './SearchResults';
import SearchSpinner from './SearchSpinner';
import { processSearchResultItemData } from '../utils/itemUtils';
import VotingList from './VotingList';
import toast from 'react-hot-toast';
import StatsWrapper from '../utils/StatsWrapper';
import { BOTIMZA_QUERY_PARAM, REF_QUERY_PARAM, REF_SHARE_BUTTON } from '../../constants';
import AdvancedSearchOptions from './AdvancedSearchOptions/AdvancedSearchOptions';
import { applyClientFilters, getAvailableFilterOptions, clearAllFilters } from '../utils/filterUtils';
import SearchSuggestions from './SearchSuggestions';
import { useSearchSuggestions } from '../hooks/useSearchSuggestions';

/**
 * Shapes a raw /api/search-style payload (searchResultsItems + featureFlags + urlAsin)
 * into the { searchResultsData, filterOptions } pair Hero needs for its search-results
 * state — shared by handleSearch's fetch path and the SSR-seeded initial-props path
 * (initialSearchResults) so both go through identical item processing.
 */
function buildSearchResultsState(results) {
  const processedItems = results.searchResultsItems.map(processSearchResultItemData);
  const searchResultsData = {
    searchResultsItems: processedItems,
    featureFlags: results.featureFlags,
    urlAsin: results.urlAsin,
  };
  const filterOptions = getAvailableFilterOptions(processedItems);
  return { searchResultsData, filterOptions };
}

function Hero({ initialSearchTerm = '', initialSearchResults = null }) {
  const hydrated = useHydrated();
  const InstructionsModal = resolvedComponents.InstructionsModal;
  // Mounting InstructionsModal directly off `hydrated` put it in the exact same commit
  // as every other hydrated-gated component (banners, ad cards, VatInfoModal, etc.) -
  // useHydrated's markHydrated() notifies all of its subscribers synchronously in one
  // pass, and that mass simultaneous first-mount is what corrupted React's hooks
  // dispatcher in production (React #321/#418/#423, confirmed live via console errors
  // on /search/{slug} pages). AuthModal/TrackItemModal/ItemFullscreenModal were already
  // fixed by mounting them only later, off their own separate state, in their own
  // commit - this mirrors that: `showInstructions` flips one animation frame after
  // `hydrated`, in Hero's own effect, not inside markHydrated()'s notify loop.
  const [showInstructions, setShowInstructions] = useState(false);
  useEffect(() => {
    if (!hydrated) return;
    const rafId = requestAnimationFrame(() => setShowInstructions(true));
    return () => cancelAnimationFrame(rafId);
  }, [hydrated]);
  const hasInitialResults = !!(
    initialSearchResults &&
    Array.isArray(initialSearchResults.searchResultsItems)
  );
  // Seeded synchronously from SSR-provided props (see SearchPage.jsx) so the SSR render
  // and the client's first hydration pass produce the same product grid without waiting
  // on an effect - renderToString never runs effects, so an effect-only bootstrap can't
  // supply the server's initial paint. Computed once via a ref (not plain assignment) -
  // this runs in the component body on every render, and initialSearchResults can hold
  // dozens of items, so recomputing processSearchResultItemData/getAvailableFilterOptions
  // on every keystroke/sort/filter change would show up as real input lag.
  const initialStateRef = useRef();
  if (initialStateRef.current === undefined) {
    initialStateRef.current = hasInitialResults ? buildSearchResultsState(initialSearchResults) : null;
  }
  const initialState = initialStateRef.current;

  const [searchQuery, setSearchQuery] = useState(initialSearchTerm);
  const [searchResults, setSearchResults] = useState(initialState?.searchResultsData);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(hasInitialResults);
  const [lastSearchTerm, setLastSearchTerm] = useState(initialSearchTerm);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [currentSortOption, setCurrentSortOption] = useState('amazon_results');
  const [selectedCurrency, setSelectedCurrency] = useState('local');
  const [currencyRates, setCurrencyRates] = useState({});
  const abortControllerRef = useRef(null);

  // Autocomplete suggestions: data fetching lives in the hook (own debounce +
  // own AbortController, separate from abortControllerRef above), keyboard
  // nav lives here since it must intercept the search <input>'s own keydowns.
  const rawSuggestions = useSearchSuggestions(searchQuery);
  const [suggestionsDismissed, setSuggestionsDismissed] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const suggestions = suggestionsDismissed ? [] : rawSuggestions;

  useEffect(() => {
    setActiveSuggestionIndex(-1);
  }, [rawSuggestions]);

  const selectSuggestion = (term, index = -1, method = 'keyboard') => {
    StatsWrapper.track('search.suggestion_picked', {
      query: searchQuery,
      selectedTerm: term,
      index,
      method,
    });
    setSearchQuery(term);
    setSuggestionsDismissed(true);
    handleSearch({ preventDefault: () => {}, query: term });
  };

  const handleSearchInputKeyDown = (e) => {
    if (suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveSuggestionIndex((prev) => (prev + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveSuggestionIndex((prev) => (prev <= 0 ? suggestions.length - 1 : prev - 1));
    } else if (e.key === 'Enter' && activeSuggestionIndex >= 0) {
      e.preventDefault();
      selectSuggestion(suggestions[activeSuggestionIndex], activeSuggestionIndex, 'keyboard');
    } else if (e.key === 'Escape') {
      setSuggestionsDismissed(true);
    }
  };

  // Cancels any in-flight /api/search fetch if Hero unmounts mid-search (e.g. client-side
  // navigation away from this page) so the response can't update state on a gone component.
  // Does NOT fire on mobile screen-lock/backgrounding - the component stays mounted then.
  useEffect(() => {
    return () => abortControllerRef.current?.abort();
  }, []);

  // Filter state management
  const [activeFilters, setActiveFilters] = useState({
    merchants: [],
    conditions: [],
    availability: [],
    priceRange: { min: null, max: null },
    domains: [],
    deliveryOptions: []
  });
  const [originalSearchResults, setOriginalSearchResults] = useState(initialState?.searchResultsData);
  const [availableFilterOptions, setAvailableFilterOptions] = useState(initialState?.filterOptions || {});

  useEffect(() => {
    // Get currency rates from window (already initialized in HomePage)
    setCurrencyRates(window.currencyRates || {});

    // SSR-hydrated /search/{slug} pages already have their initial state seeded via
    // props (above) - skip both the localStorage flag and the ?q= fetch below, since
    // there's nothing to fetch (these URLs carry no ?q= parameter anyway) and hasSearched
    // is already correct. These loads never go through handleSearch, so they'd otherwise
    // never report 'search.results_rendered' - fire it here instead, once, on mount.
    if (hasInitialResults) {
      StatsWrapper.track('search.results_rendered', { searchTerm: initialSearchTerm });
      return;
    }

    // Check local storage for search flag
    const searched = localStorage.getItem('hasSearched') === 'true';
    setHasSearched(searched);

    // Check for search query in URL parameters
    const params = new URLSearchParams(window.location.search);
    const query = params.get(BOTIMZA_QUERY_PARAM);
    if (query) {
      setSearchQuery(query);
      handleSearch({ preventDefault: () => {}, query }); // Pass query to handleSearch
    }
  }, []);

  const containsHebrew = (text) => {
    const hebrewPattern = /[\u0590-\u05FF]/;
    return hebrewPattern.test(text);
  };


  const handleSearch = async (e) => {
    e.preventDefault();
    const searchTerm = e.query || searchQuery; // Use passed query or state
    if (!searchTerm.trim()) return;

    // Update Hebrew validation to use toast
    if (containsHebrew(searchTerm)) {
      toast.error('נא להזין את החיפוש באנגלית בלבד', {
        duration: 3000,
        position: 'top-center',
        style: {
          direction: 'rtl',
          background: '#333',
          color: '#fff',
        },
      });
      return;
    }

    setIsLoading(true);

    // Update last search term
    setLastSearchTerm(searchTerm);

    // Abort any still-in-flight search (e.g. from a previous keystroke-triggered
    // submit or the ?q= bootstrap effect) so its response can't land after this one,
    // and so handleClearSearch has something to cancel.
    abortControllerRef.current?.abort();
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      // Get ref from URL parameters
      const params = new URLSearchParams(window.location.search);
      const ref = params.get(REF_QUERY_PARAM);

      const response = await fetch(`/api/search?${BOTIMZA_QUERY_PARAM}=${searchTerm}${ref ? `&${REF_QUERY_PARAM}=${ref}` : ''}`, {
        signal: abortController.signal,
      });
      const results = await response.json();

      if (!results || typeof results !== 'object') {
        console.error('Invalid results format received, results:', results);
        setSearchResults(null);
        return;
      }

      if (
        results &&
        results.searchResultsItems &&
        Array.isArray(results.searchResultsItems)
      ) {
        // Store feature flags in window for use by other components
        if (results.featureFlags) {
          window.featureFlags = { ...window.featureFlags, ...results.featureFlags };
          document.dispatchEvent(new CustomEvent('featureFlagsUpdated'));
        }

        const { searchResultsData, filterOptions } = buildSearchResultsState(results);

        // Store original results for filtering
        setOriginalSearchResults(searchResultsData);
        setSearchResults(searchResultsData);
        setAvailableFilterOptions(filterOptions);
        
        // Clear any existing filters when performing new search
        setActiveFilters(clearAllFilters());
        
        localStorage.setItem('hasSearched', 'true'); // Set search flag in local storage
        setHasSearched(true); // Update state

        // Push the search term into the URL so it's shareable/bookmarkable and the
        // existing ?q= useEffect above can pre-fill it on load. pushState (not
        // navigation) so no re-render or re-fetch is triggered.
        window.history.pushState({}, '', `/?${BOTIMZA_QUERY_PARAM}=${encodeURIComponent(searchTerm)}`);

        // Confirms the client actually rendered what the server returned -
        // distinct from (and downstream of) the server-side 'search.succeeded'
        // event, which only knows the response was sent, not that it reached
        // the DOM. Fired here, not in an effect keyed on `searchResults`,
        // because that state is also replaced by sort/filter (below) and
        // would double-count a single search.
        StatsWrapper.track('search.results_rendered', { searchTerm });
      } else {
        console.error('Invalid search results structure');
        setSearchResults(null);
        setOriginalSearchResults(null);
        setAvailableFilterOptions({});
      }
    } catch (error) {
      if (error.name === 'AbortError') return; // cancelled via handleClearSearch or a newer search
      console.error('Search failed:', error);
      // You might want to show an error message to the user
    } finally {
      if (abortControllerRef.current === abortController) {
        setIsLoading(false);
      }
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    abortControllerRef.current?.abort();
    setIsLoading(false);

    const params = new URLSearchParams(window.location.search);
    params.delete(BOTIMZA_QUERY_PARAM);
    const newSearch = params.toString();
    window.history.pushState({}, '', `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`);
  };

  const toggleAdvancedOptions = () => {
    setShowAdvancedOptions(!showAdvancedOptions);
  };

  // Handle sort change from SortMenu
  const handleSortChange = (sortOption, applySortingFn) => {
    setCurrentSortOption(sortOption);
    if (searchResults && searchResults.searchResultsItems) {
      const sortedItems = applySortingFn(searchResults.searchResultsItems, sortOption);
      setSearchResults({ searchResultsItems: sortedItems });
    }
  };

  // Handle currency change from CurrencyMenu
  const handleCurrencyChange = (currency, rates) => {
    setSelectedCurrency(currency);
    setCurrencyRates(rates);
  };

  // Handle filter change from FilterMenu
  const handleFilterChange = (newFilters) => {
    setActiveFilters(newFilters);
    
    // Apply filters to original search results
    if (originalSearchResults && originalSearchResults.searchResultsItems) {
      const filteredItems = applyClientFilters(originalSearchResults.searchResultsItems, newFilters);
      setSearchResults({ searchResultsItems: filteredItems });
    }
  };

  // Clear all filters
  const handleClearFilters = () => {
    const clearedFilters = clearAllFilters();
    setActiveFilters(clearedFilters);
    
    // Reset to original search results
    if (originalSearchResults) {
      setSearchResults(originalSearchResults);
    }
  };

  // Create instance of AdvancedSearchOptions
  const advancedSearchOptions = AdvancedSearchOptions({ 
    showAdvancedOptions, 
    toggleAdvancedOptions,
    onSortChange: handleSortChange,
    onCurrencyChange: handleCurrencyChange,
    onFilterChange: handleFilterChange,
    activeFilters,
    availableFilterOptions
  });

  return (
    <section className="text-white pb-20">
      {/* Resolved by preloadHydratedChunks() before `hydrated` can be true - see
          hydrationPreload.js. Mounted off `showInstructions`, not `hydrated` directly -
          see the comment where that state is declared above. */}
      {showInstructions && InstructionsModal && <InstructionsModal />}

      {/* The big brand title above the search box is rendered by Header
          (titleVariant="hero" on the Layout of every page that mounts Hero) - not
          here - so Header stays the single owner of the brand title and can never
          stack its nav logo on top of it. */}
      <form
        onSubmit={handleSearch}
        className="max-w-3xl mx-auto text-left"
        dir="ltr"
        aria-label="Search Form"
      >
        <div className="relative flex flex-row items-center bg-white rounded-lg shadow-lg">
          {searchResults && searchQuery === lastSearchTerm ? (
            <button
              type="button"
              onClick={() => {
                const shareText = `בוא תימצא! ביצעתי השוואת מחירים עבור *${searchQuery}* ולא תאמינו מה גיליתי!`;
                // Add ref=share_button to the shared URL
                const shareUrl = `${window.location.origin}${window.location.pathname}?${BOTIMZA_QUERY_PARAM}=${encodeURIComponent(searchQuery)}&${REF_QUERY_PARAM}=${REF_SHARE_BUTTON}`;
                navigator.share
                  ? navigator.share({ text: shareText, url: shareUrl })
                  : alert('Sharing not supported');
                StatsWrapper.track('search.share_link', {
                  searchQuery,
                });
              }}
              className="px-3 py-3 md:px-6 md:py-4 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-opacity-50"
              aria-label="Share"
            >
              <Share2 className="w-5 h-5 md:w-6 md:h-6 text-gray-600" />
            </button>
          ) : (
            <button
              type="submit"
              className="px-3 py-3 md:px-6 md:py-4 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-opacity-50"
              disabled={isLoading}
              aria-label="Search"
            >
              <Search className="w-5 h-5 md:w-6 md:h-6 text-gray-600" />
            </button>
          )}
          <input
            type="text"
            placeholder="Search item or paste a link to amazon"
            className="w-full pl-2 pr-3 py-3 md:px-6 md:py-4 text-gray-800 focus:outline-none"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setSuggestionsDismissed(false);
            }}
            onKeyDown={handleSearchInputKeyDown}
            style={{ direction: 'ltr' }}
            aria-label="Search Query"
            autoComplete="off"
            role="combobox"
            aria-expanded={suggestions.length > 0}
            aria-controls="search-suggestions-listbox"
            aria-activedescendant={
              activeSuggestionIndex >= 0 ? `search-suggestion-${activeSuggestionIndex}` : undefined
            }
            name="search"
          />
          {advancedSearchOptions.renderToggleButton()}
          {searchQuery && (
            <>
              <div className="w-px h-6 bg-gray-300 mx-1 md:mx-2"></div>
              <button
                type="button"
                onClick={handleClearSearch}
                className="px-3 md:px-4 focus:outline-none hover:text-gray-800 transition-colors"
                aria-label="Clear Search"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </>
          )}
          <SearchSuggestions
            query={searchQuery}
            suggestions={suggestions}
            activeIndex={activeSuggestionIndex}
            onSelect={selectSuggestion}
            onClose={() => setSuggestionsDismissed(true)}
          />
        </div>
      </form>

      {/* Advanced Search Options Menu */}
      {advancedSearchOptions.renderDropdownMenu()}

      {isLoading ? (
        <SearchSpinner />
      ) : !!searchResults ? (
        <SearchResults 
          items={searchResults?.searchResultsItems} 
          featureFlags={searchResults?.featureFlags}
          urlAsin={searchResults?.urlAsin}
          searchTerm={lastSearchTerm} 
          sortOption={currentSortOption}
          selectedCurrency={selectedCurrency}
          currencyRates={currencyRates}
          activeFilters={activeFilters}
          originalItemsCount={originalSearchResults?.searchResultsItems?.length || 0}
        />
      ) : (
        hasSearched && <VotingList />
      )}
    </section>
  );
}

export default Hero;
